import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { paths } from "../../paths";
import spiritFooterBg from "../../assets/footer-bg.jpg"; // tennis court background photo
import sticker from "../../assets/footer-sticker.png"; // "EVERY Game LEAVES A Mark" text asset
import spiritLogo from "../../assets/spirit-logo.png"; // white SPIRIT '26 logo image

// Added relative, group, inline-block, w-fit and pb-1 so lines sit nicely beneath the text
const linkCls =
  "group relative inline-block w-fit text-[11px] font-semibold text-white/90 hover:text-white font-sans uppercase tracking-wider text-left pb-1";

// 1. Same-Page Section Anchors (Scrolls down landing page)
const NAVIGATION_LINKS = [
  { label: "About", hash: "#about" },
  { label: "Gallery", hash: "#gallery" },
  { label: "Responsibilities", hash: "#responsibility" },
  { label: "Contact", hash: "#footer" },
  { label: "FAQ", hash: "#faq" },
];

// 2. Separate Pages / Sub-routes. On the About page itself, "About Us" would
// link to the current page, so it swaps to "Home" there.
const quickLinksFor = (pathname) => [
  pathname === paths.about
    ? { label: "Home", to: paths.home }
    : { label: "About Us", to: paths.about },
  { label: "Rulebook", to: paths.rulebook },
];

const ACCOUNT_LINKS = [
  { label: "Sign Up", to: paths.signup },
  { label: "Login", to: paths.login },
];

const SOCIAL_LINKS = [
  { label: "Instagram", href: "https://www.instagram.com/spirit_iitguwahati/" },
  { label: "Linkedin", href: "https://www.linkedin.com/company/spiritiitguwahati/" },
  { label: "Youtube", href: "https://www.youtube.com/@spiritiitguwahati2541" },
];

const Footer = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const quickLinks = quickLinksFor(pathname);
  // Anchor targets (#about, #gallery, ...) exist only on the landing page.
  const showNavigation = pathname === paths.home;

  // The anchor targets only exist on the landing page. There, smooth-scroll in
  // place; from any other page (e.g. About Us), go home carrying the hash and
  // the landing page scrolls to the section on arrival.
  const scrollToHash = (hash) => (e) => {
    e.preventDefault();
    if (pathname === paths.home) {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`${paths.home}${hash}`);
    }
  };

  return (
    <footer className="relative w-full overflow-hidden select-none bg-[#1c0a02]">
      {/* Inject custom transition logic to handle separate hover-in and hover-out speeds exactly like your header */}
      <style>{`
        /* HOVER IN: Green stretches first, Orange follows, Blue covers last */
        .group:hover .line-blue { transition-duration: 600ms !important; }
        .group:hover .line-orange { transition-duration: 450ms !important; }
        .group:hover .line-green { transition-duration: 300ms !important; }

        /* HOVER OUT: Blue collapses first, Orange follows, Green leaves last */
        .line-blue { transition-duration: 300ms !important; }
        .line-orange { transition-duration: 450ms !important; }
        .line-green { transition-duration: 600ms !important; }
      `}</style>

      {/* Full-bleed background image */}
      <img
        src={spiritFooterBg}
        alt="Sports Fest Background"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
      />

      {/* Content Overlay */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 sm:px-8 md:px-14 pt-16 md:pt-20 pb-10 flex flex-col justify-between gap-12 min-h-[500px] lg:min-h-[560px]">
        {/* UPPER: Navigation links */}
        <div className="flex justify-center md:justify-end w-full">
          {/* 4 columns on the landing page; 3 elsewhere (Navigation hidden) */}
          <div className={`grid grid-cols-2 gap-x-4 gap-y-10 ${showNavigation ? "sm:grid-cols-4" : "sm:grid-cols-3"} sm:gap-14 md:gap-14 lg:gap-14 text-left w-full sm:w-auto`}>

            {/* Column 1: Navigation (same-page anchors — landing page only,
                since the #about/#gallery/... sections exist only there) */}
            {showNavigation && (
              <div className="flex flex-col gap-2">
                <h4 className="text-[14px] font-bold uppercase tracking-[0.15em] text-white font-sans opacity-95">
                  Navigation
                </h4>
                <div className="flex flex-col items-start gap-4">
                  {NAVIGATION_LINKS.map((l) => (
                    <a
                      key={l.label}
                      href={l.hash}
                      className={linkCls}
                      onClick={scrollToHash(l.hash)}
                    >
                      <span>{l.label}</span>
                      <span aria-hidden="true" className="line-green absolute left-0 bottom-0 h-[3px] w-full bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                      <span aria-hidden="true" className="line-orange absolute left-0 bottom-0 h-[3px] w-full bg-[#CC5E15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                      <span aria-hidden="true" className="line-blue absolute left-0 bottom-0 h-[3px] w-full bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Column 2: Quick Links (Now routes pages natively without breakages) */}
            <div className="flex flex-col gap-2">
              <h4 className="text-[14px] font-bold uppercase tracking-[0.15em] text-white font-sans opacity-95">
                Quick Links
              </h4>
              <div className="flex flex-col items-start gap-4">
                {quickLinks.map((l) => (
                  <Link key={l.label} to={l.to} className={linkCls}>
                    <span>{l.label}</span>
                    <span aria-hidden="true" className="line-green absolute left-0 bottom-0 h-[3px] w-full bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    <span aria-hidden="true" className="line-orange absolute left-0 bottom-0 h-[3px] w-full bg-[#CC5E15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    <span aria-hidden="true" className="line-blue absolute left-0 bottom-0 h-[3px] w-full bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 3: Account */}
            <div className="flex flex-col gap-2">
              <h4 className="text-[14px] font-bold uppercase tracking-[0.15em] text-white font-sans opacity-95">
                Account
              </h4>
              <div className="flex flex-col items-start gap-4">
                {ACCOUNT_LINKS.map((l) => (
                  <Link key={l.label} to={l.to} className={linkCls}>
                    <span>{l.label}</span>
                    <span aria-hidden="true" className="line-green absolute left-0 bottom-0 h-[3px] w-full bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    <span aria-hidden="true" className="line-orange absolute left-0 bottom-0 h-[3px] w-full bg-[#CC5E15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    <span aria-hidden="true" className="line-blue absolute left-0 bottom-0 h-[3px] w-full bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 4: Social */}
            <div className="flex flex-col gap-2">
              <h4 className="text-[14px] font-bold uppercase tracking-[0.15em] text-white font-sans opacity-95">
                Social
              </h4>
              <div className="flex flex-col items-start gap-4">
                {SOCIAL_LINKS.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className={linkCls}
                  >
                    <span>{l.label}</span>
                    <span aria-hidden="true" className="line-green absolute left-0 bottom-0 h-[3px] w-full bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    <span aria-hidden="true" className="line-orange absolute left-0 bottom-0 h-[3px] w-full bg-[#CC5E15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                    <span aria-hidden="true" className="line-blue absolute left-0 bottom-0 h-[3px] w-full bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM: logo / sticker / email */}
        <div className="flex flex-col gap-8 md:grid md:grid-cols-3 items-center w-full pt-8 md:pt-16">
          <div className="flex justify-center md:justify-start items-center">
            <img
              src={spiritLogo}
              alt="SPIRIT '26 Logo"
              className="h-10 md:h-[44px] w-auto object-contain block"
            />
          </div>

          <div className="flex justify-center items-center">
            <img
              src={sticker}
              alt="EVERY Game LEAVES A Mark"
              className="w-[180px] md:w-[221px] h-auto object-contain block opacity-95"
            />
          </div>

          <div className="flex justify-center md:justify-end items-center">
            <a
              href="mailto:spirit@iitg.ac.in"
              className="group relative inline-block text-sm md:text-[15px] font-bold tracking-wide text-white font-sans pb-1"
            >
              <span>Spirit@iitg.ac.in</span>
              <span aria-hidden="true" className="line-green absolute left-0 bottom-0 h-[3px] w-full bg-[#727d1f] z-10 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
              <span aria-hidden="true" className="line-orange absolute left-0 bottom-0 h-[3px] w-full bg-[#CC5E15] z-20 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
              <span aria-hidden="true" className="line-blue absolute left-0 bottom-0 h-[3px] w-full bg-[#019D9D] z-30 transform scale-x-0 origin-right transition-transform ease-out group-hover:origin-left group-hover:scale-x-100" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;